// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the corner assistant's one question about an agent chat (design/agents-task-linking E1):
// it finished without a ClickUp task and Konvo was not sure which. "This chat has no ClickUp task
// yet", how long the agent worked, Konvo's two best options with Link, "Both, split by turn" when
// the chat covered both, "Other task" (the chat's Time and client panel) and "Not now". Linking
// happens in place and writes nothing to ClickUp. Asked once per chat (corner kind agent_task).

import {Routes} from '@app/app/Routes';
import {SuggestionCard, taskProblem, timeAmount} from '@app/features/konvo_agents/components/AgentTimeTasks';
import AgentSidePanel from '@app/features/konvo_agents/state/AgentSidePanel';
import AgentTime from '@app/features/konvo_agents/state/AgentTime';
import own from '@app/features/konvo_corner/components/CornerAgentTaskCard.module.css';
import styles from '@app/features/konvo_corner/components/CornerCard.module.css';
import Corner from '@app/features/konvo_corner/state/Corner';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {XIcon} from '@app/features/ui/icons/Icons';
import type {CornerSuggestion} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const WORKED = msg({
	message: 'The agent worked {time} on it. Which task is it for?',
	comment: 'Corner question about an agent chat without a ClickUp task: how long the agent worked, e.g. "2h 14m".',
});

export const CornerAgentTaskCard = observer(
	({
		suggestion,
		variant,
		className,
	}: {
		suggestion: CornerSuggestion;
		variant: 'desktop' | 'phone';
		className?: string;
	}) => {
		const {i18n, t} = useLingui();
		const machineId = suggestion.action.context?.['agent_machine'] ?? '';
		const threadId = suggestion.action.context?.['agent_thread'] ?? '';
		const title = suggestion.action.context?.['agent_chat'] ?? null;
		const [busy, setBusy] = useState<string | null>(null);
		const [error, setError] = useState<string | null>(null);
		useEffect(() => {
			if (machineId && threadId) void AgentTime.loadKept(machineId, threadId);
		}, [machineId, threadId]);
		const view = AgentTime.of(machineId, threadId)?.view ?? null;
		const options = view?.suggest && !view.suggest.dismissed ? view.suggest.options.slice(0, 2) : [];
		// Linked meanwhile (here, in the panel or by Konvo Assistant): nothing left to ask.
		const linked = !!view && (view.tasks?.length ?? 0) > 0;
		useEffect(() => {
			if (linked && !busy) Corner.answered('linked_elsewhere');
		}, [linked, busy]);
		const act = async (key: string, change: () => Promise<unknown>) => {
			setBusy(key);
			setError(null);
			try {
				await change();
				Corner.answered(key.startsWith('link') ? 'link' : key);
			} catch (problem) {
				setError(taskProblem(i18n, problem));
				setBusy(null);
			}
		};
		const openPanel = () => {
			Corner.answered('other');
			RouterUtils.transitionTo(Routes.agents(machineId, threadId));
			AgentSidePanel.open(`${machineId}/${threadId}`, {kind: 'time'});
		};
		const reason = view ? i18n._(WORKED, {time: timeAmount(i18n, view.workedMs)}) : suggestion.reason;
		return (
			<section
				className={clsx(
					styles.card,
					variant === 'phone' ? styles.phone : clsx(styles.desktop, own.desktop),
					own.card,
					className,
				)}
				aria-label={t({
					message: 'Question from Konvo',
					comment: 'Accessible name of the corner question about an agent chat’s ClickUp task.',
				})}
				tabIndex={-1}
				onMouseEnter={() => Corner.hover(true)}
				onMouseLeave={() => Corner.hover(false)}
				data-kind={suggestion.kind}
				data-flx="konvo-corner.card"
			>
				<div className={styles.top}>
					<KonvoLogo size={18} />
					<span className={clsx(styles.context, own.context)}>
						<b>Konvo</b>
						{title && <span> · {title}</span>}
					</span>
					<button
						type="button"
						className={styles.iconButton}
						aria-label={t({message: 'Close', comment: 'Close button on a corner assistant pop-up.'})}
						onClick={() => Corner.dismiss()}
						data-flx="konvo-corner.card.close"
					>
						<XIcon size={16} />
					</button>
				</div>
				<p className={styles.title} data-flx="konvo-corner.card.title">
					{t({
						message: 'This chat has no ClickUp task yet',
						comment: 'Corner question when an agent chat finished without a ClickUp task and Konvo was not sure which.',
					})}
				</p>
				<p className={own.body} data-flx="konvo-corner.card.reason">
					{reason}
				</p>
				{options.length > 0 && (
					<div className={own.options} data-flx="konvo-corner.agent-task.options">
						{options.map((option) => (
							<SuggestionCard
								key={option.task.id}
								option={option}
								phone={variant === 'phone'}
								tone="corner"
								busy={busy === `link:${option.task.id}`}
								onLink={() =>
									void act(`link:${option.task.id}`, () =>
										AgentTime.changeTasks(machineId, threadId, {op: 'link', taskId: option.task.id}),
									)
								}
							/>
						))}
					</div>
				)}
				{error && <p className={own.error}>{error}</p>}
				<div className={clsx(styles.actions, own.actions)}>
					{view?.suggest?.split && options.length === 2 && (
						<button
							type="button"
							className={clsx(styles.secondary, own.small)}
							disabled={busy !== null}
							onClick={() =>
								void act('both', () =>
									AgentTime.changeTasks(machineId, threadId, {
										op: 'both',
										taskIds: options.map((option) => option.task.id),
									}),
								)
							}
							data-flx="konvo-corner.agent-task.both"
						>
							{t({
								message: 'Both, split by turn',
								comment: 'Corner question: link both suggested tasks; each turn goes to the one its prompt was about.',
							})}
						</button>
					)}
					<button
						type="button"
						className={clsx(styles.secondary, own.small, own.ghost)}
						onClick={openPanel}
						data-flx="konvo-corner.agent-task.other"
					>
						{t({
							message: 'Other task',
							comment: 'Corner question: open the chat’s Time and client panel to pick another task.',
						})}
					</button>
					<span className={own.grow} />
					<button
						type="button"
						className={clsx(styles.secondary, own.small, own.ghost)}
						onClick={() => Corner.notNow()}
						data-flx="konvo-corner.card.later"
					>
						{t({
							message: 'Not now',
							comment: 'Corner question about an agent chat’s ClickUp task: put it away (it is asked once).',
						})}
					</button>
				</div>
			</section>
		);
	},
);
